import { memo, useMemo, Activity, useCallback } from "react";
import { Flex, Button, Icon, Icons, Switch } from "@ir/ui";
import { useElementSize } from "@/hooks";
import HasAnyPermission from "@/presentation/workspace/HasAnyPermission";
import { WorkspacePermissions } from "@/core/permissions";
import DeductionFilterSet from "@/presentation/deduction/components/DeductionFilter/DeductionFilterSet";
import type { FilterSetApi } from "@/presentation/deduction/components/DeductionFilter/DeductionFilterSet";

interface ApprovalsToolbarProps {
  isSearchOrFilterApplied: boolean;
  filterApi: FilterSetApi;
  isRefreshing: boolean;
  hasSelectedApprovals: boolean;
  groupByCheck: boolean;
  onGroupByCheckChange: (groupByCheck: boolean) => void;
  onBulkRejection: () => void;
  onBulkApproval: () => void;
  onClearSelection: () => void;
  onRefresh: () => void;
  onOpenFilter: () => void;
}

const ApprovalsToolbar = memo(({
  isSearchOrFilterApplied,
  filterApi,
  isRefreshing,
  hasSelectedApprovals,
  groupByCheck,
  onGroupByCheckChange,
  onBulkRejection,
  onBulkApproval,
  onClearSelection,
  onRefresh,
  onOpenFilter,
}: ApprovalsToolbarProps) => {
  const [bulkActionsRef, bulkActionsSize] = useElementSize<HTMLDivElement>(true, hasSelectedApprovals);

  const mainContentWidth = useMemo(
    () => (hasSelectedApprovals ? bulkActionsSize.contentWidth : 0),
    [hasSelectedApprovals, bulkActionsSize.contentWidth]
  );

  const handleGroupByCheck = useCallback(
    (mode: boolean) => {
      onGroupByCheckChange(mode);
    },
    [onGroupByCheckChange]
  );

  return (
    <Flex direction="row" align="center" justifyContent="end" wrap="no-wrap" gap={1} className="my-3">
      {isSearchOrFilterApplied && (
        <Flex
          direction="row"
          align="center"
          justifyContent="end"
          wrap="no-wrap"
          className="py-0 flex-1"
          style={{ width: `calc(100% - ${mainContentWidth}px)` }}
        >
          <DeductionFilterSet
            className="w-100"
            groupByCheck={groupByCheck}
            openFilter={onOpenFilter}
            filterApi={filterApi}
          />
        </Flex>
      )}

      <Switch
        className="group-by-check-switch"
        size="small"
        defaultSelected={groupByCheck}
        onChange={handleGroupByCheck}
        aria-label="Group by Check"
        role="switch"
        isSelected={groupByCheck}
        isDisabled={isRefreshing}
      >
        Group by Check
      </Switch>

      <Activity name="approvals-actions" mode={hasSelectedApprovals ? "visible" : "hidden"}>
        <Flex
          ref={bulkActionsRef}
          direction="row"
          align="center"
          justifyContent="end"
          wrap="no-wrap"
        >
          <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTIONS_APPROVAL_ACTION]}>
            <Button size="small" variant="clear" color="grey" onPress={onBulkRejection} isDisabled={isRefreshing}>
              <Icon name={Icons.AlertCircle} size="small" slot="start" /> Return
            </Button>
            <Button size="small" variant="clear" color="grey" onPress={onBulkApproval} isDisabled={isRefreshing}>
              <Icon name={Icons.CheckDone} size="small" slot="start" /> Approve
            </Button>
          </HasAnyPermission>
          <Button size="small" variant="clear" color="grey" onPress={onClearSelection}>
            <Icon name={Icons.CheckSquare} size="small" slot="start" /> Clear selection
          </Button>
        </Flex>
      </Activity>

      <Button
        size="small"
        variant="clear"
        color="grey"
        isDisabled={isRefreshing}
        onPress={onRefresh}
      >
        <Icon
          name={Icons.RefreshCw03}
          size="small"
          slot="start"
          className={isRefreshing ? "icon-fast-loading" : ""}
        />
        Refresh
      </Button>
    </Flex>
  );
});

ApprovalsToolbar.displayName = "ApprovalsToolbar";

export { ApprovalsToolbar };
